import React from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import i18n from 'i18n-js';
import Shimmer from 'react-native-shimmer';
import moment from 'moment';
import { WebView } from 'react-native-webview';

import { machineStatusColorName, machineStatusName, machineStatuses } from './functions';
import useManageItemLine from '@components/wallboard/hooks/useManageItemLine';
import { accumulateTimeByStatus, buildTimelineContentHtml } from '@components/wallboard/helpers/itemLineTimeline';

const TimeToday = ({ C, machineStatus, font, convertTime, formattedEvents = [], isSameDay, wallboardTimelineEndDate }) => {
  const { status } = machineStatus;

  const lastEvent = formattedEvents[formattedEvents.length - 1];
  const isSameDayResult = isSameDay(wallboardTimelineEndDate);
  const nowDiffSeconds =
    lastEvent && isSameDayResult ? moment.utc().diff(moment.utc(lastEvent.endedOn), 'seconds') : undefined;

  const { timeEventsStorage, highlightedStatus } = accumulateTimeByStatus(
    formattedEvents,
    status,
    isSameDayResult,
    nowDiffSeconds
  );

  const render = [machineStatuses.ACTIVE, machineStatuses.CHANGE, machineStatuses.SETUP];
  return (
    <View style={{ flexDirection: 'row' }}>
      {render.map((i, index) => {
        const isHighlighted = highlightedStatus === i;
        return (
          <View
            style={[
              {
                paddingHorizontal: 8,
                borderRadius: 13,
                marginRight: index == render.length - 1 ? 0 : 29,
                backgroundColor: isHighlighted ? C(machineStatusColorName[i]) : 'transparent',
              },
            ]}
            key={`Time-${i}-${index}`}
          >
            <Text
              style={[
                { fontSize: 20 },
                font['Title 1'],
                {
                  color: isHighlighted ? C('Label/Light/AlwaysLight Primary') : C(machineStatusColorName[i]),
                  fontVariant: ['tabular-nums'],
                },
              ]}
            >
              {convertTime(timeEventsStorage[i], true)}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

const Time = ({ C, machineStatus, font, convertTime }) => {
  const { lastUpdatedOn, status } = machineStatus;

  const time = moment.utc();

  const runningTime = time.diff(lastUpdatedOn ? moment.utc(lastUpdatedOn) : moment.utc(), 's');

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <Text
        style={[
          { fontSize: 16 },
          font['Headline'],
          { color: C(machineStatusColorName[status]), fontVariant: ['tabular-nums'], marginRight: 12, paddingTop: 7 },
        ]}
      >
        {i18n.t(machineStatusName[status])}
      </Text>
      <Text style={[{ fontSize: 20 }, font['Title 1'], { color: C(machineStatusColorName[status]), fontVariant: ['tabular-nums'] }]}>
        {convertTime(runningTime, true)}
      </Text>
    </View>
  );
};

const RenderLine = React.memo(
  ({
    formattedEvents,
    onMessage,
    lineWidth,
    wallboardSelectedLevel,
    C,
    renderProps,
    status,
    secondWidth,
    machineId,
    wallboardTimelineEndDate,
    isSameDay,
  }) => {
    const isSameDayResult = isSameDay(wallboardTimelineEndDate);
    const colorFn = (s) => C(machineStatusColorName[s]);
    const lineContent = buildTimelineContentHtml(
      formattedEvents,
      secondWidth,
      status,
      colorFn,
      renderProps,
      isSameDayResult
    );

    return (
      <WebView
        key={`Machine-${machineId}`}
        androidHardwareAccelerationDisabled
        style={{ height: 32, backgroundColor: C('Background/Light/Chart'), opacity: 0.99 }}
        onMessage={onMessage}
        source={{
          html: `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Document</title>
      <style>

      .wrapper{

      }

      .lineComponent {
        display: flex;
        height: 32px;
        width: ${lineWidth}px;
        border-radius: 3px;
        background-color: ${C('Background/Light/Chart')};
        flex-direction: row;
        overflow: hidden;
        ${wallboardSelectedLevel == 'last24' && isSameDayResult ? 'justify-content:flex-end; white-space:nowrap;' : ''}
      }

      body{
        width: 100%;
        margin: 0;
        padding: 0;
      }

      #shimmer {
        display: flex;
        width: 100%;
        -webkit-mask:linear-gradient(-60deg,#000 30%,#0005,#000 70%) right/300% 100%;
        animation: shimmer 2.5s infinite;
      }

      @keyframes shimmer {
        100% {-webkit-mask-position:left}
      }

      .timeLine {
        display: flex;
        width: 100%,
        flex-direction: row;
      }

      .timeNumbers{
        display: flex;
        width: 100%;
        justify-content: space-between;
        flex-direction: row;
      }

      .Wallboard-Active:hover div {
        opacity: 0.9;
        box-shadow: 0px 1.2px 3.6px 0px rgba(0, 0, 0, 0.14), 0px 6.4px 14.4px 0px rgba(0, 0, 0, 0.15), 0px 0px 30px 0px rgba(0, 0, 0, 0.07);
      }

      .Wallboard-NotProductive:hover div {
        opacity: 0.9;
        box-shadow: 0px 1.2px 3.6px 0px rgba(0, 0, 0, 0.14), 0px 6.4px 14.4px 0px rgba(0, 0, 0, 0.15), 0px 0px 30px 0px rgba(0, 0, 0, 0.07);
      }

      .Wallboard-Setup:hover div {
        opacity: 0.9;
        box-shadow: 0px 1.2px 3.6px 0px rgba(0, 0, 0, 0.14), 0px 6.4px 14.4px 0px rgba(0, 0, 0, 0.15), 0px 0px 30px 0px rgba(0, 0, 0, 0.07);
      }

      .Wallboard-Change:hover div {
        opacity: 0.9;
        box-shadow: 0px 1.2px 3.6px 0px rgba(0, 0, 0, 0.14), 0px 6.4px 14.4px 0px rgba(0, 0, 0, 0.15), 0px 0px 30px 0px rgba(0, 0, 0, 0.07);
      }

      </style>
    </head>
    <script>


    const intervalId = setInterval(() => {
      const storage = document.getElementsByClassName("last-item");
      for (let i = 0; i <= storage.length; i++) {
        const item = storage[i];
        if (item) {
          const width = parseFloat(item.style.width);
          item.style.width = width + ${secondWidth} + "px";
        }
      }
    }, 1000);

    function onMouseOver(event, type, item){

      const width = event.offsetWidth;
      const rect = event.getBoundingClientRect();
      const position = {
        top: rect.top,
        left: rect.left,
      };

      function onBridgeReady(cb) {
        if (window.postMessage.length !== 1) {
          setTimeout(function () {
            onBridgeReady(cb);
          }, 100);
        } else {
          cb();
        }
      }

      var payload = JSON.stringify({position, width, item, type});

      if(window.ReactNativeWebView && window.ReactNativeWebView.postMessage){
        window.ReactNativeWebView.postMessage(payload);
      }else{
        if (window.opener) {
          // Web new window
          window.opener.postMessage(payload, window.opener.origin);
          window.close();
        } else if (window.parent) {
          // Web iframe
          window.parent.postMessage(payload, window.parent.origin);
        } else {
          // React Native
          onBridgeReady(function () {
            window.postMessage(payload);
          });
        }
      }
    }
    </script>
    <body>
      <div class="wrapper">
        <div class="lineComponent">
          ${lineContent}
        </div>
      </div>
    </body>
    </html>`,
        }}
      />
    );
  },
  (prev, next) =>
    prev?.C?.schemeName === next?.C?.schemeName &&
    prev.lineWidth === next.lineWidth &&
    prev.status === next.status &&
    prev.wallboardSelectedLevel === next.wallboardSelectedLevel &&
    prev.formattedEvents.length === next.formattedEvents.length
);

const TimeWrapper = ({
  C,
  machineStatus,
  font,
  convertTime,
  formattedEvents,
  wallboardSelectedLevel,
  status,
  machineId,
  wallboardTimelineEndDate,
  isSameDay,
  useForceUpdateEverySecond,
}) => {
  useForceUpdateEverySecond();

  return wallboardSelectedLevel == 'today' || (wallboardSelectedLevel == 'last24' && !isSameDay(wallboardTimelineEndDate)) ? (
    <TimeToday
      {...{
        C,
        machineStatus,
        font,
        convertTime,
        formattedEvents,
        wallboardTimelineEndDate,
        isSameDay,
      }}
    />
  ) : (
    <Time
      {...{
        C,
        machineStatus,
        font,
        convertTime,
        formattedEvents,
      }}
    />
  );
};

export default (props) => {
  const {
    C,
    font,
    convertTime,
    timeLineLoaded,
    wallboardSelectedLevel,
    wallboardTimelineEndDate,
    localTime,
    isSameDay,
    useForceUpdateEverySecond,
  } = props;

  const {
    machineId,
    shortName,
    status,
    machineStatus,
    formattedEvents,
    renderProps,
    onMessage,
    lineWidth,
    hourWidth,
    minWidth,
    secondWidth,
  } = useManageItemLine(props);

  return (
    <View
      style={{
        width: '100%',
        marginBottom: 36,
        zIndex: 1000000 - renderProps.machineIndex,
      }}
      key={machineId + '-' + renderProps.machineIndex}
    >
      <View
        style={{
          width: '100%',
          justifyContent: 'space-between',
          flexDirection: 'row',
        }}
      >
        <View style={{ flexDirection: 'row', height: 42, alignItems: 'center' }}>
          <View
            style={{
              height: 18,
              width: 18,
              backgroundColor: C(machineStatusColorName[status]),
              borderRadius: 18,
            }}
          />
          <View style={{ marginLeft: 8 }}>
            <Text
              style={[
                font['Large Title'],
                {
                  color: C('Label/Light/Primary'),
                  fontSize: 32,
                  lineHeight: 42,
                  letterSpacing: 0.39,
                },
              ]}
            >
              {shortName}
            </Text>
          </View>
        </View>
        <View>
          <TimeWrapper
            {...{
              C,
              machineStatus,
              font,
              convertTime,
              formattedEvents,
              wallboardSelectedLevel,
              status,
              machineId,
              isSameDay,
              wallboardTimelineEndDate,
              useForceUpdateEverySecond,
            }}
          />
        </View>
      </View>
      <View>
        <TouchableOpacity activeOpacity={1}>
          {timeLineLoaded?.[machineId] ? (
            formattedEvents.length ? (
              <RenderLine
                {...{
                  onMessage,
                  lineWidth,
                  wallboardSelectedLevel,
                  formattedEvents,
                  C,
                  status,
                  secondWidth,
                  renderProps,
                  machineId,
                  wallboardTimelineEndDate,
                  isSameDay,
                }}
              />
            ) : (
              <View
                style={{
                  width: '100%',
                  height: 32,
                  backgroundColor: C('Gray/Light/3'),
                  justifyContent: 'center',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text>{i18n.t('No events')}</Text>
              </View>
            )
          ) : (
            <Shimmer>
              <View
                style={{
                  width: '100%',
                  height: 32,
                  backgroundColor: C('Gray/Light/3'),
                }}
              />
            </Shimmer>
          )}
        </TouchableOpacity>
        {wallboardSelectedLevel == 'today' && (
          <View style={{ width: '100%', flexDirection: 'row' }}>
            {Array.from({ length: 24 }).map((_, i) => (
              <View style={{ width: hourWidth, height: 25, position: 'relative' }} key={`machine-time-${i}`}>
                <View
                  style={{
                    alignItems: 'center',
                    width: 22,
                    position: 'absolute',
                    left: -11,
                  }}
                >
                  <View
                    style={{
                      width: 1,
                      height: 4,
                      backgroundColor: C('Label/Light/Tertiary'),
                    }}
                  />
                  <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary') }]}>{i}</Text>
                </View>
                {i === 23 && (
                  <View
                    style={{
                      alignItems: 'center',
                      width: 22,
                      position: 'absolute',
                      right: -11,
                    }}
                  >
                    <View
                      style={{
                        width: 1,
                        height: 4,
                        backgroundColor: C('Label/Light/Tertiary'),
                      }}
                    />
                    <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary') }]}>{24}</Text>
                  </View>
                )}
              </View>
            ))}
          </View>
        )}
        {wallboardSelectedLevel == 'last24' && (
          <View style={{ width: '100%', flexDirection: 'row' }}>
            {Array.from({ length: 24 }).map((_, i) => {
              const time = isSameDay(wallboardTimelineEndDate) ? localTime() : localTime(wallboardTimelineEndDate);
              const isLast = i === 23;
              const isFirst = i === 0;
              const minLast = Number(time.format('mm'));
              const minFirst = 60 - minLast;
              const width = isLast ? minLast * minWidth : hourWidth;

              return [
                isFirst && (
                  <View
                    style={{
                      width: minFirst * minWidth,
                      height: 25,
                      position: 'relative',
                    }}
                    key={`machine-time-${i}-first`}
                  />
                ),
                <View style={{ width: width, height: 25, position: 'relative' }} key={`machine-time-${i}`}>
                  <View
                    style={{
                      alignItems: 'center',
                      width: 22,
                      position: 'absolute',
                      left: -11,
                    }}
                  >
                    <View
                      style={{
                        width: 1,
                        height: 4,
                        backgroundColor: C('Label/Light/Tertiary'),
                      }}
                    />
                    <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary') }]}>
                      {time
                        .subtract(1, 'd')
                        .add(i + 1, 'h')
                        .format('HH')}
                    </Text>
                  </View>
                </View>,
              ];
            })}
          </View>
        )}
      </View>
    </View>
  );
};
